import React from 'react'
import { NavLink } from 'react-router-dom'
import { 
  LayoutDashboard, 
  ArrowRightLeft, 
  Receipt, 
  User, 
  Settings,
  Landmark,
  MessageCircle,
  X,
  Users,
  Clock,
  Activity,
  BarChart3,
  Sliders
} from 'lucide-react'

import { useAuth } from '../../context/AuthContext'

const Sidebar = ({ isOpen, onClose }) => {
  const { isAdmin } = useAuth()

  const userNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/transactions', icon: Receipt, label: 'Transactions' },
    { path: '/transfer', icon: ArrowRightLeft, label: 'Transfer Money' },
    { path: '/profile', icon: User, label: 'Profile Settings' }, // Merged into one
    { path: '/settings', icon: Settings, label: 'Settings' }
  ]

  const adminNavItems = [
    { path: '/admin-portal', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin-portal/chat', icon: MessageCircle, label: 'User Chat' },
    { path: '/admin-portal/pending-approvals', icon: Clock, label: 'Pending Approvals', badge: true },
    { path: '/admin-portal/users', icon: Users, label: 'Users' },
    { path: '/admin-portal/transactions', icon: Receipt, label: 'Transactions' },
    { path: '/admin-portal/activity', icon: Activity, label: 'Activity Logs' },
    { path: '/admin-portal/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin-portal/settings', icon: Sliders, label: 'System Settings' }
  ]

  const navItems = isAdmin ? adminNavItems : userNavItems

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 z-50 h-screen w-64
          bg-white dark:bg-dark-800 border-r border-gray-200 dark:border-dark-700
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                <Landmark className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                  UniversalTrustcu
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isAdmin ? 'Admin Portal' : 'Banking Portal'}
                </p>
              </div>
            </div>

            {/* Close button (mobile) */}
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon className={`w-5 h-5 ${isActive ? 'text-blue-600 dark:text-blue-400' : ''}`} />
                    <span className="font-medium">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        3
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-dark-700">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg">
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                Need Help?
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Contact our 24/7 support team
              </p>
              <button className="w-full btn btn-sm btn-primary">
                Get Support
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}

export default Sidebar